import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePutEncAfterBlitzModalStore } from './PutEncAfterBlitzModalStore';

import { usePutEncAfterBlitz } from 'hooks/InteractiveMap/usePutEncAfterBlitz';
import MLTextArea from 'components/Input/TextArea';
import { encUpdateAfterBlitzSchema, EncUpdateAfterBlitzFormValues } from 'api/encounters/types';
import { MLInput } from 'components/Input/Input';
import { MLButton } from 'components/Button/Button';
import { MLSelect } from 'components/Select/Select';
import useGetAvailableTechnicians from 'hooks/Object/useGetAvailableTechnicians';

interface PutEncAfterBlitzModalProps {
  refetchEncounters: () => void;
}

const PutEncAfterBlitzModal: React.FC<PutEncAfterBlitzModalProps> = ({ refetchEncounters }) => {
  const { isOpen, onManagePutEncAfterBlitzClose, encId } = usePutEncAfterBlitzModalStore();
  const { t } = useTranslation();
  const { slug } = useParams();
  const { data: availableTechnicians } = useGetAvailableTechnicians();
  const putEncAfterBlitzMutation = usePutEncAfterBlitz();
  const { control, handleSubmit, reset } = useForm<EncUpdateAfterBlitzFormValues>({
    resolver: zodResolver(encUpdateAfterBlitzSchema),
    defaultValues: {
      creation_comment: '',
      photos: [],
      responsible_person: 0,
    },
  });
  const onReset = useCallback(() => {
    reset();
    onManagePutEncAfterBlitzClose();
  }, [reset, onManagePutEncAfterBlitzClose]);

  if (!isOpen) return null;

  const responsibleOptions = (availableTechnicians ?? []).map(responsePerson => ({
    value: responsePerson.id,
    label: responsePerson.label || responsePerson.email,
  }));
  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '20px',
        }}
        onClick={onReset}
      >
        {/* Modal Content */}
        <div
          style={{
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            maxWidth: '800px',
            width: 344,
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={e => e.stopPropagation()}
          onWheel={e => e.stopPropagation()}
          onTouchMove={e => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px 24px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h5 style={{ margin: 0, fontWeight: 600, color: '#333' }}>Add Pest Sighting</h5>
            <button
              onClick={onReset}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ×
            </button>
          </div>
          {/* Body */}

          <div style={{ padding: '8px 24px 24px 24px', flex: 1, overflowY: 'auto' }}>
            {/* Row-1 */}
            <div className="row mb-2">
              <div className="col-xl-12">
                <Controller
                  control={control}
                  name="responsible_person"
                  rules={{ required: t('MapModals.AddVul.required') }}
                  render={({ field, fieldState: { error } }) => (
                    <MLSelect
                      required
                      {...field}
                      error={error?.message}
                      label={t('MapModals.AddEnc.step2.selectResponsible')}
                      value={field.value}
                      onChange={field.onChange}
                      options={responsibleOptions}
                    />
                  )}
                />
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-xl-12">
                <Controller
                  control={control}
                  name="creation_comment"
                  render={({ field, fieldState: { error } }) => (
                    <MLTextArea
                      {...field}
                      className="form-control"
                      error={error?.message}
                      label="Comment"
                      placeholder="Complete encounter with comment"
                      rows={3}
                    />
                  )}
                />
              </div>
            </div>

            {/* Row-2 */}
            <div className="row mb-3">
              <Controller
                name="photos"
                control={control}
                render={({ field }) => (
                  <MLInput
                    label="Photo"
                    type="file"
                    multiple
                    onChange={e => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      field.onChange(files.length > 0 ? files : null);
                    }}
                  />
                )}
              />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px 24px 24px',
              borderTop: '1px solid #eee',
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
            }}
          >
            <MLButton
              color="success"
              onClick={handleSubmit(async formData => {
                putEncAfterBlitzMutation.mutate(
                  { encId, enc: formData, objectSlug: slug || '' },
                  {
                    onSuccess: () => {
                      refetchEncounters();
                      onReset();
                    },
                  },
                );
              })}
              innerText="Complete Encounter"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PutEncAfterBlitzModal;
